// @ts-check
// Respostas prontas (contrato 10 de atendimento, seções 12.1 e 24.4): as da escola e dos departamentos em
// Configurações > Atendimento e as pessoais na Caixa ("Minhas respostas prontas"). Título, atalho para o "/", texto
// com as variáveis (inseridas por botão, com prévia de exemplo) e as ações depois do envio (situação, categoria,
// etiquetas e transferência).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { createReplyTemplate, deleteReplyTemplate, listReplyTemplates, listServiceTeams, updateReplyTemplate } from '../../data/service-desk.js';
import { SERVICE_TEMPLATE_VARIABLES, renderServiceTemplate } from '../../../../shared/service-desk/reply-template-variables.js';
import { SERVICE_CATEGORIES, SERVICE_TAGS } from '../../../../shared/service-desk/ticket-values.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Checkbox, EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { serviceErrorMessage, showServiceError, ticketValueLabel } from './service-display.js';
import { t } from '../../i18n/index.js';

/** Valores de exemplo da prévia (a resposta real usa os do atendimento, pelo servidor). */
const SAMPLE_VALUES = Object.freeze({
  'guardian.firstName': 'Mariana', 'guardian.name': 'Mariana Lopes', 'student.firstName': 'Lucas', 'student.name': 'Lucas Lopes', 'student.gradeLevel': '6º ano',
  'student.classOffering': '6º A', 'ticket.protocol': '2026-001234', 'ticket.subject': 'Declaração de matrícula', 'team.name': 'Secretaria',
  'team.nextOpeningAt': 'amanhã às 8h', 'agent.firstName': 'Ana', 'institution.name': 'Colégio Horizonte',
});

/** @param {string} name */
export const templateVariableLabelKey = (name) => `serviceDesk.templateVariables.${name.replace('.', '_')}`;

/**
 * @param {{ template: any | null, scope: 'SCHOOL_AND_TEAM' | 'PERSONAL', teams: any[], onCancel: () => void, onSaved: () => void }} props
 */
function TemplateEditor({ template, scope, teams, onCancel, onSaved }) {
  const [templateScope, setTemplateScope] = useState(template?.scope ?? (scope === 'PERSONAL' ? 'PERSONAL' : 'SCHOOL'));
  const [teamId, setTeamId] = useState(template?.teamId ?? teams[0]?.id ?? '');
  const [title, setTitle] = useState(template?.title ?? '');
  const [shortcut, setShortcut] = useState(template?.shortcut ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [actions, setActions] = useState(/** @type {Record<string, any>} */ (template?.actions ?? {}));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const preview = renderServiceTemplate(body, SAMPLE_VALUES);
  const isShortcutValid = !shortcut || /^[a-z0-9-]{2,30}$/.test(shortcut);
  const save = async () => {
    setIsSaving(true);
    setError(null);
    const cleanedActions = Object.fromEntries(Object.entries(actions).filter(([, value]) => value !== '' && value !== null && !(Array.isArray(value) && value.length === 0)));
    const input = { title: title.trim(), shortcut: shortcut || null, body, actions: cleanedActions, ...(templateScope === 'TEAM' ? { teamId } : {}) };
    try {
      if (template) await updateReplyTemplate(template.id, input);
      else await createReplyTemplate({ ...input, scope: templateScope });
      showSuccessSnackbar(t('serviceDesk.replyTemplates.saved'));
      onSaved();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return html`<div class="service-settings-card service-template-editor" data-testid="service-reply-template-editor">
    ${scope !== 'PERSONAL' && !template
      ? html`<div class="form-field-row">
          <label class="form-field"><span class="form-field-label">${t('serviceDesk.replyTemplates.scope')}</span>
            <select class="text-input" value=${templateScope} onChange=${(/** @type {Event} */ event) => setTemplateScope(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
              <option value="SCHOOL">${t('serviceDesk.replyTemplates.scopes.SCHOOL')}</option>
              <option value="TEAM">${t('serviceDesk.replyTemplates.scopes.TEAM')}</option>
            </select></label>
          ${templateScope === 'TEAM'
            ? html`<label class="form-field"><span class="form-field-label">${t('serviceDesk.replyTemplates.team')}</span>
                <select class="text-input" value=${teamId} onChange=${(/** @type {Event} */ event) => setTeamId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                  ${teams.filter((team) => team.teamKind === 'STANDARD').map((team) => html`<option key=${team.id} value=${team.id}>${team.name}</option>`)}
                </select></label>`
            : null}
        </div>`
      : null}
    <div class="form-field-row">
      <label class="form-field"><span class="form-field-label">${t('serviceDesk.replyTemplates.title')}</span>
        <input class="text-input" value=${title} maxLength=${80} onInput=${(/** @type {Event} */ event) => setTitle(/** @type {HTMLInputElement} */ (event.currentTarget).value)} /></label>
      <label class="form-field"><span class="form-field-label">${t('serviceDesk.replyTemplates.shortcut')}</span>
        <span class="service-number-field"><span class="service-number-suffix">/</span><input class="text-input" value=${shortcut} maxLength=${30} data-error=${isShortcutValid ? undefined : 'true'}
          onInput=${(/** @type {Event} */ event) => setShortcut(/** @type {HTMLInputElement} */ (event.currentTarget).value.toLowerCase())} /></span>
        <span class="input-hint">${t('serviceDesk.replyTemplates.shortcutHint')}</span></label>
    </div>
    <label class="form-field"><span class="form-field-label">${t('serviceDesk.replyTemplates.body')}</span>
      <textarea class="text-input service-textarea" rows="4" maxLength=${4096} value=${body}
        onInput=${(/** @type {Event} */ event) => setBody(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea></label>
    <div class="service-variable-buttons" aria-label=${t('serviceDesk.replyTemplates.variables')}>
      ${SERVICE_TEMPLATE_VARIABLES.map((name) => html`<button key=${name} type="button" class="service-chip-button" onClick=${() => setBody(`${body}{{${name}}}`)}>
        <${Icon} name="IconPlus" size=${12} />${t(templateVariableLabelKey(name))}
      </button>`)}
    </div>
    <div class="service-template-preview">
      <span class="form-field-label">${t('serviceDesk.replyTemplates.preview')}</span>
      <p>${preview.text || t('serviceDesk.replyTemplates.emptyPreview')}</p>
    </div>
    <h4 class="service-panel-subtitle">${t('serviceDesk.replyTemplates.actions')}</h4>
    <div class="service-policy-grid">
      <label class="form-field"><span class="form-field-label">${t('serviceDesk.replyTemplates.actionStatus')}</span>
        <select class="text-input" value=${actions.status ?? ''} onChange=${(/** @type {Event} */ event) => setActions({ ...actions, status: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
          <option value="">${t('serviceDesk.replyTemplates.noChange')}</option>
          ${['WAITING_FAMILY', 'RESOLVED'].map((value) => html`<option key=${value} value=${value}>${ticketValueLabel('status', value)}</option>`)}
        </select></label>
      <label class="form-field"><span class="form-field-label">${t('serviceDesk.replyTemplates.actionCategory')}</span>
        <select class="text-input" value=${actions.category ?? ''} onChange=${(/** @type {Event} */ event) => setActions({ ...actions, category: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
          <option value="">${t('serviceDesk.replyTemplates.noChange')}</option>
          ${SERVICE_CATEGORIES.map((value) => html`<option key=${value} value=${value}>${ticketValueLabel('category', value)}</option>`)}
        </select></label>
      <label class="form-field"><span class="form-field-label">${t('serviceDesk.replyTemplates.actionTransfer')}</span>
        <select class="text-input" value=${actions.transferToTeamId ?? ''} onChange=${(/** @type {Event} */ event) => setActions({ ...actions, transferToTeamId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
          <option value="">${t('serviceDesk.replyTemplates.noChange')}</option>
          ${teams.filter((team) => team.teamKind === 'STANDARD').map((team) => html`<option key=${team.id} value=${team.id}>${team.name}</option>`)}
        </select></label>
    </div>
    <div class="service-category-flags">
      ${SERVICE_TAGS.map((tag) => html`<label key=${tag} class="service-check-row">
        <${Checkbox} checked=${(actions.addTags ?? []).includes(tag)} ariaLabel=${ticketValueLabel('tags', tag)}
          onChange=${(checked) => setActions({ ...actions, addTags: checked ? [...(actions.addTags ?? []), tag] : (actions.addTags ?? []).filter((/** @type {string} */ value) => value !== tag) })} />
        <span>${t('serviceDesk.replyTemplates.addTag', { tag: ticketValueLabel('tags', tag) })}</span>
      </label>`)}
    </div>
    ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
    <div class="service-inline-actions">
      <${Button} title=${t('common.cancel')} size="small" variant="secondary" onClick=${onCancel} />
      <${Button} title=${t('serviceDesk.replyTemplates.save')} accent="blue" size="small" isLoading=${isSaving} disabled=${!title.trim() || !body.trim() || !isShortcutValid} onClick=${save} />
    </div>
  </div>`;
}

/**
 * @param {{ scope: 'SCHOOL_AND_TEAM' | 'PERSONAL', canEdit: boolean }} props
 */
export function ReplyTemplatesManager({ scope, canEdit }) {
  const [templates, setTemplates] = useState(/** @type {any[] | null} */ (null));
  const [teams, setTeams] = useState(/** @type {any[]} */ ([]));
  const [editing, setEditing] = useState(/** @type {any | 'new' | null} */ (null));
  const load = () => listReplyTemplates()
    .then((list) => setTemplates(list.filter((template) => (scope === 'PERSONAL' ? template.scope === 'PERSONAL' : template.scope !== 'PERSONAL'))))
    .catch(() => setTemplates([]));
  useEffect(() => {
    load();
    listServiceTeams().then(setTeams).catch(() => {});
  }, []);
  /** @param {any} template */
  const remove = async (template) => {
    try {
      await deleteReplyTemplate(template.id);
      showSuccessSnackbar(t('serviceDesk.replyTemplates.deleted'));
      load();
    } catch (error) {
      showServiceError(error);
    }
  };
  const content = html`<div class="service-reply-templates" data-testid="service-reply-templates">
    ${templates === null
      ? html`<${Skeleton} height=${120} />`
      : templates.length === 0 && editing !== 'new'
        ? html`<${EmptyState} icon="IconMessage" title=${t(`serviceDesk.replyTemplates.empty.${scope}`)} subtitle=${t('serviceDesk.replyTemplates.emptyHint')} />`
        : html`<ul class="service-settings-list">${templates.map((template) => editing?.id === template.id
          ? html`<li key=${template.id} class="service-template-editing"><${TemplateEditor} template=${template} scope=${scope} teams=${teams} onCancel=${() => setEditing(null)}
              onSaved=${() => {
                setEditing(null);
                load();
              }} /></li>`
          : html`<li key=${template.id} class="service-template-row">
              <div class="service-template-texts">
                <span class="service-settings-list-title">${template.title}${template.shortcut ? html`<code>/${template.shortcut}</code>` : null}</span>
                <span class="input-hint">${template.scope === 'TEAM' ? teams.find((team) => team.id === template.teamId)?.name ?? t('serviceDesk.replyTemplates.scopes.TEAM') : t(`serviceDesk.replyTemplates.scopes.${template.scope}`)}
                  ${template.useCount ? ` · ${t('serviceDesk.replyTemplates.useCount', { count: template.useCount })}` : ''}</span>
                <span class="service-template-body">${template.body}</span>
              </div>
              ${canEdit
                ? html`<span class="service-note-actions">
                    <${LightIconButton} icon="IconPencil" ariaLabel=${t('serviceDesk.replyTemplates.edit')} onClick=${() => setEditing(template)} />
                    <${LightIconButton} icon="IconTrash" ariaLabel=${t('serviceDesk.replyTemplates.delete')} onClick=${() => remove(template)} />
                  </span>`
                : null}
            </li>`)}</ul>`}
    ${editing === 'new'
      ? html`<${TemplateEditor} template=${null} scope=${scope} teams=${teams} onCancel=${() => setEditing(null)} onSaved=${() => {
          setEditing(null);
          load();
        }} />`
      : canEdit ? html`<${Button} title=${t('serviceDesk.replyTemplates.create')} icon="IconPlus" size="small" variant="secondary" onClick=${() => setEditing('new')} />` : null}
  </div>`;
  if (scope === 'PERSONAL') return content;
  return html`<div class="service-settings-tab-body" data-testid="service-settings-replies">
    <${SettingsSection} title=${t('serviceDesk.replyTemplates.settingsTitle')} description=${t('serviceDesk.replyTemplates.settingsDescription')}>${content}<//>
  </div>`;
}
